import * as z from 'zod';

import { CompanyWireSettingsId, PayoffId, PropertyId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { InterestAttribution } from '../PaymentDue';
import { zodBrandedUuid } from '../utils/Zod';
import { Fee } from './Charges';
import {
  LoanStatusActive,
  LoanStatusForeclosure,
  LoanStatusPendingTransfer,
  LoanStatusPreActive,
  LoanStatusReo,
} from './LoanStatus';
import { PaymentMethod } from './Payments';

/* TRACKED payoffs */
// Record added to payoff at time status is updated to PROCESSED (aka payoff is recorded)
// This will be the data used for the transaction and balances
export const TrackedPayoffAllowedPaymentMethods = z.enum([
  PaymentMethod.enum.wire,
  PaymentMethod.enum.check,
  PaymentMethod.enum.phone,
  PaymentMethod.enum.balanceReallocation,
]);
export type TrackedPayoffAllowedPaymentMethods = z.infer<typeof TrackedPayoffAllowedPaymentMethods>;

/*
  Outstanding amounts due and balances may have changed since the original payoff was generated,
  so this is a record of those amounts at time of recording.

  Note: the balances are from time of recording and do NOT account for any waivers
*/
export const PayoffWaiver = z.object({ escrow: z.number(), suspense: z.number(), principal: z.number().default(0) });
export type PayoffWaiver = z.infer<typeof PayoffWaiver>;

export const ShortageActionType = z.enum(['waive-suspense', 'waive-escrow', 'no-waive']);
export type ShortageActionType = z.infer<typeof ShortageActionType>;

export const PaymentDueTotalsSnapshot = z.object({
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  reserveCredit: z.number(),
  escrowShortage: z.number(),
});
export type PaymentDueTotalsSnapshot = z.infer<typeof PaymentDueTotalsSnapshot>;

const PayoffRecord = z.object({
  createdAt: zodJSInstantDate, // Date the lender records the payment
  processedDate: zodJSPlainDate, // Date lender selects as the "Date received"
  paymentMethod: TrackedPayoffAllowedPaymentMethods,
  outstandingPrincipal: z.number(),
  outstandingInterest: z.number(),
  interestAttribution: InterestAttribution.array().optional(),
  fee: Fee.optional(),
  escrowBalance: z.number(),
  suspenseBalance: z.number(),
  outstandingFeeAmount: z.number(), // Does not include payoff fees which cannot be edited from original record
  outstandingAdvanceAmount: z.number(), // advance principal amount remaining
  outstandingAdvanceInterestAmount: z.number(),
  reserveBalance: z.number(),
  totalDue: z.number(), // Includes outstanding fees + advances
  amountPaid: z.number(),
  waivedAmounts: PayoffWaiver,
  additionalPrincipal: z.number().optional(),
  additionalEscrow: z.number().optional(),
  additionalSuspense: z.number().optional(),
  unfulfilledPaymentDueTotalsAtTimeOfPayoff: PaymentDueTotalsSnapshot.optional(), // Should always be present on new payoffs, may not be present before approx February 2025
  deferredDelinquentInterestAmount: z.number().optional(),
  defermentWorkoutAmount: z.number().optional(),
  /**
   * @deprecated Use deferredPrincipalBalance instead
   */
  defermentPrincipalModificationAmount: z.number().optional(), // Can't delete, but this is deprecate this in favor of deferredPrincipalBalance
  deferredPrincipalBalance: z.number().optional(),
});
export type PayoffRecord = z.infer<typeof PayoffRecord>;

const PayoffStatusPending = z.literal('pending');
const PayoffStatusExpired = z.literal('expired');
const PayoffStatusProcessed = z.literal('processed');
const PayoffStatusReversed = z.literal('reversed');
export const PayoffStatus = z.union([
  PayoffStatusPending,
  PayoffStatusExpired,
  PayoffStatusProcessed,
  PayoffStatusReversed,
]);
export type PayoffStatus = z.infer<typeof PayoffStatus>;

// Outstanding amounts due + balances at time of generation
const BasePayoff = z.object({
  id: zodBrandedUuid<PayoffId>(),
  createdAt: zodJSInstantDate,
  expirationDate: zodJSPlainDate,
  wireSettingsId: zodBrandedUuid<CompanyWireSettingsId>().optional(),
  outstandingPrincipal: z.number(),
  outstandingInterest: z.number(),
  interestAttribution: InterestAttribution.array().optional(),
  fee: Fee.optional(), // Payoff-specific fees
  outstandingFeeAmount: z.number(), // Fees already on the loan
  outstandingAdvanceAmount: z.number(), // Advances already on the loan
  escrowBalance: z.number(),
  suspenseBalance: z.number(),
  reserveBalance: z.number(),
  totalDue: z.number(),
  isPartial: z.boolean().optional(),
  calculateFromFundingDate: z.boolean().optional(),
  propertyIds: z.array(zodBrandedUuid<PropertyId>()).optional(),
  status: z.union([PayoffStatusPending, PayoffStatusExpired]),
  reallocateDIFromSuspense: z.boolean().optional(), // If true, the delinquent interest will be reallocated from available suspense funds as displayed in the payoff letter ONLY
});
export type BasePayoff = z.infer<typeof BasePayoff>;

// A processed record is added to the original payoff after being recorded
export const ProcessedPayoff = BasePayoff.extend({
  status: PayoffStatusProcessed,
  processedRecord: PayoffRecord,
});
export type ProcessedPayoff = z.infer<typeof ProcessedPayoff>;

export const ReversedPayoff = BasePayoff.extend({
  status: PayoffStatusReversed,
  processedRecord: PayoffRecord,
});
export type ReversedPayoff = z.infer<typeof ReversedPayoff>;

export type ProcessedOrReversedPayoff = ProcessedPayoff | ReversedPayoff;

export function isProcessedPayoff(payoff: Payoff): payoff is ProcessedPayoff {
  return payoff.status === 'processed';
}
export function isReversedPayoff(payoff: Payoff): payoff is ReversedPayoff {
  return payoff.status === 'reversed';
}
export function isProcessedOrReversedPayoff(payoff: Payoff): payoff is ProcessedPayoff | ReversedPayoff {
  return isProcessedPayoff(payoff) || isReversedPayoff(payoff);
}
export function isProcessedFullPayoff(payoff: Payoff): payoff is ProcessedPayoff {
  return isProcessedPayoff(payoff) && !payoff.isPartial;
}
export function isProcessedPartialPayoff(payoff: Payoff): payoff is ProcessedPayoff {
  return isProcessedPayoff(payoff) && !!payoff.isPartial;
}

export function isReversedFullPayoff(payoff: Payoff): payoff is ReversedPayoff {
  return payoff.status === 'reversed' && !payoff.isPartial;
}

export const Payoff = z.union([BasePayoff, ProcessedPayoff, ReversedPayoff]);
export type Payoff = z.infer<typeof Payoff>;

export const PayoffAllowedStatuses = z.union([
  LoanStatusPreActive,
  LoanStatusActive,
  LoanStatusPendingTransfer,
  LoanStatusForeclosure,
  LoanStatusReo,
]);

export const PaidOffType = z.union([z.literal('untrackedPayoff'), z.literal('payoff')]);
export type PaidOffType = z.infer<typeof PaidOffType>;

const UntrackedPaidOffRecord = z.object({
  id: zodBrandedUuid<PayoffId>(),
  payoffDate: zodJSInstantDate,
  payoffType: z.literal('untrackedPayoff'),
});
const TrackedPaidOffRecord = z.object({
  id: zodBrandedUuid<PayoffId>(),
  payoffDate: zodJSPlainDate,
  payoffType: z.literal('payoff'),
});

export const PaidOffRecord = z.discriminatedUnion('payoffType', [UntrackedPaidOffRecord, TrackedPaidOffRecord]);
export type PaidOffRecord = z.infer<typeof PaidOffRecord>;
